import React, { useEffect, useState, useMemo } from "react";
import { api, formatINR, API, TOKEN_KEY, blurOnWheel } from "../lib/api";
import { Download, AlertTriangle, Clock, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";

/** Phase 5 — Reconciliation report panel.
 *  Filters: site, category, vendor, date range (all default to "ALL").
 *  Period is keyed to request.created_at (what was raised in the period).
 *  Payments outside the period are flagged "paid_outside_period" so the
 *  paid total isn't mistaken for cash-out-this-exact-month. */

const STATUS_LABEL = {
  pending: "PENDING", approved: "APPROVED", rejected: "REJECTED",
  partially_paid: "PARTIAL", recorded: "PAID", paid: "PAID",
  closed: "CLOSED", failed: "FAILED",
};

const STATUS_CLS = {
  pending: "bg-slate-100 text-slate-700 border-slate-300",
  approved: "bg-olive/10 text-olive border-olive/40",
  rejected: "bg-red-50 text-red-700 border-red-300",
  partially_paid: "bg-amber-50 text-amber-800 border-amber-400",
  recorded: "bg-emerald-50 text-emerald-700 border-emerald-400",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-400",
  closed: "bg-slate-50 text-slate-600 border-slate-300",
  failed: "bg-red-50 text-red-700 border-red-400",
};

const FLAG_META = {
  aging: { label: "AGING", cls: "bg-red-100 text-red-700 border-red-400", icon: Clock },
  under_paid: { label: "UNDER-PAID", cls: "bg-orange-100 text-orange-700 border-orange-400", icon: AlertTriangle },
  over_paid: { label: "OVER-PAID", cls: "bg-orange-100 text-orange-700 border-orange-400", icon: AlertTriangle },
  paid_outside_period: { label: "PAID-OUT-OF-RANGE", cls: "bg-slate-200 text-slate-700 border-slate-400", icon: Clock },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`;
  } catch { return iso.slice(0, 10); }
};

export default function ReconciliationPanel({ sites = [], categories = [] }) {
  const [filters, setFilters] = useState({
    from: "", to: "", site: "", category: "", vendor_id: "", aging_days: 5,
  });
  const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showLeakage, setShowLeakage] = useState(false);

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.from) p.set("from", filters.from);
    if (filters.to) p.set("to", filters.to);
    if (filters.site) p.set("site", filters.site);
    if (filters.category) p.set("category", filters.category);
    if (filters.vendor_id) p.set("vendor_id", filters.vendor_id);
    if (filters.aging_days) p.set("aging_days", String(filters.aging_days));
    return p.toString();
  }, [filters]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/reconciliation?${queryString}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reconciliation");
    } finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/reports/reconciliation.csv?${queryString}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.href = url;
      a.download = m ? m[1] : "vow_reconciliation.csv";
      document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally { setDownloading(false); }
  };

  const clearFilters = () => {
    setFilters({ from: "", to: "", site: "", category: "", vendor_id: "", aging_days: 5 });
  };

  const totals = data?.totals;
  const items = data?.line_items || [];
  const leakage = data?.leakage || [];
  const flags = data?.flag_counts || {};
  const anyPaidOutside = items.some((r) => (r.flags || []).includes("paid_outside_period"));

  return (
    <div className="space-y-4">
      {/* ===== Filters ===== */}
      <div data-testid="recon-filters" className="bg-white border border-slate-200 p-4 lg:p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-micro">RECONCILIATION · FILTERS</div>
            <div className="font-mono text-[10px] text-slate-500 mt-0.5">
              Period keyed to request created_at — "what was raised in the period"
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="recon-clear"
              onClick={clearFilters}
              className="font-mono text-[10px] uppercase tracking-widest text-slate-600 hover:text-black underline"
            >
              CLEAR
            </button>
            <button
              data-testid="recon-run"
              onClick={run}
              disabled={loading}
              className="bg-black text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-50"
            >
              {loading ? "LOADING…" : "RUN"}
            </button>
            <button
              data-testid="recon-csv"
              onClick={downloadCsv}
              disabled={downloading || !data}
              className="border border-slate-200 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white disabled:opacity-50 flex items-center gap-1"
            >
              <Download size={11} /> {downloading ? "…" : "CSV"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <FilterDate label="FROM" testid="recon-from" value={filters.from} onChange={(v) => setFilters((s) => ({ ...s, from: v }))} />
          <FilterDate label="TO"   testid="recon-to"   value={filters.to}   onChange={(v) => setFilters((s) => ({ ...s, to: v }))} />
          <FilterSelect label="SITE" testid="recon-site" value={filters.site} onChange={(v) => setFilters((s) => ({ ...s, site: v }))} options={sites} />
          <FilterSelect label="CATEGORY" testid="recon-category" value={filters.category} onChange={(v) => setFilters((s) => ({ ...s, category: v }))} options={categories} />
          <FilterVendor testid="recon-vendor" value={filters.vendor_id} onChange={(v) => setFilters((s) => ({ ...s, vendor_id: v }))} vendors={vendors} />
          <div>
            <div className="label-micro mb-1">AGING (DAYS)</div>
            <input
              data-testid="recon-aging"
              type="number" min="0" step="1"
              onWheel={blurOnWheel}
              value={filters.aging_days}
              onChange={(e) => setFilters((s) => ({ ...s, aging_days: parseInt(e.target.value || "0") }))}
              className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none"
            />
          </div>
        </div>
      </div>

      {/* ===== KPI totals ===== */}
      {totals && (
        <div data-testid="recon-totals" className="grid grid-cols-2 lg:grid-cols-5 gap-0 border-t border-l border-black">
          <KPI label="TOTAL REQUESTED" value={formatINR(totals.requested)} sub={`${totals.count} items`} />
          <KPI label="TOTAL APPROVED"  value={formatINR(totals.approved)} />
          <KPI label="TOTAL PAID"       value={formatINR(totals.paid)} alert={anyPaidOutside ? "Some payments fall outside the selected period" : null} />
          <KPI label="OUTSTANDING"      value={formatINR(totals.outstanding)} hint="Approved − Paid" danger={totals.outstanding > 0} />
          <KPI label="TRIMMED / REJECTED" value={formatINR(totals.trimmed)} hint="Requested − Approved" />
        </div>
      )}

      {/* ===== Flag counters ===== */}
      {totals && (
        <div className="flex flex-wrap gap-2" data-testid="recon-flag-summary">
          <FlagPill label={`AGING > ${filters.aging_days}D`}  count={flags.aging || 0}    danger />
          <FlagPill label="PAID ≠ APPROVED" count={flags.mismatch || 0} danger />
          <FlagPill label="LEAKAGE"           count={flags.leakage || 0}  danger
                    onClick={() => leakage.length && setShowLeakage((s) => !s)} />
        </div>
      )}

      {/* ===== Leakage drawer ===== */}
      {showLeakage && leakage.length > 0 && (
        <div data-testid="recon-leakage" className="bg-red-50 border-2 border-red-400 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-red-700" size={14} />
            <span className="label-micro text-red-700">LEAKAGE · {leakage.length} ROW{leakage.length === 1 ? "" : "S"}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-[11px]">
              <thead className="text-slate-500">
                <tr className="text-left border-b border-red-300">
                  <th className="py-1 pr-2">EXPENSE</th><th className="py-1 pr-2">REQ</th>
                  <th className="py-1 pr-2">VENDOR</th><th className="py-1 pr-2 text-right">AMOUNT</th>
                  <th className="py-1 pr-2">MODE</th><th className="py-1 pr-2">REASON</th>
                </tr>
              </thead>
              <tbody>
                {leakage.map((x) => (
                  <tr key={x.expense_id} className="border-b border-red-200 last:border-b-0">
                    <td className="py-1 pr-2 font-bold">{x.expense_id}</td>
                    <td className="py-1 pr-2">{x.request_id || "—"}</td>
                    <td className="py-1 pr-2">{x.vendor_name || "—"}</td>
                    <td className="py-1 pr-2 text-right">{formatINR(x.amount)}</td>
                    <td className="py-1 pr-2">{x.payment_mode}</td>
                    <td className="py-1 pr-2 text-red-700">{x.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Line items ===== */}
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200/10 flex items-center justify-between">
          <div className="label-micro">LINE ITEMS · {items.length}</div>
          {anyPaidOutside && (
            <div className="font-mono text-[10px] text-slate-500 flex items-center gap-1">
              <Clock size={11} />
              <span>* paid total includes payments made outside the selected period</span>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full font-mono text-[11px]">
            <thead className="text-slate-500 bg-slate-50">
              <tr className="text-left border-b border-slate-200">
                <th className="px-3 py-2">REQ ID</th>
                <th className="px-3 py-2">DATE</th>
                <th className="px-3 py-2">SITE</th>
                <th className="px-3 py-2">CATEGORY</th>
                <th className="px-3 py-2">VENDOR</th>
                <th className="px-3 py-2 text-right">REQUESTED</th>
                <th className="px-3 py-2 text-right">APPROVED</th>
                <th className="px-3 py-2 text-right">PAID</th>
                <th className="px-3 py-2 text-right">REMAINING</th>
                <th className="px-3 py-2">STATUS</th>
                <th className="px-3 py-2">FLAGS</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={11} className="text-center py-10 label-micro text-slate-400">NO DATA</td></tr>
              )}
              {items.map((r) => (
                <tr
                  key={r.request_id}
                  data-testid={`recon-row-${r.request_id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${(r.flags || []).includes("aging") ? "bg-red-50/40" : ""}`}
                >
                  <td className="px-3 py-2 font-bold whitespace-nowrap">{r.request_id}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2">{r.site}</td>
                  <td className="px-3 py-2">{r.category}</td>
                  <td className="px-3 py-2 max-w-[160px] truncate" title={r.vendor_name}>{r.vendor_name}</td>
                  <td className="px-3 py-2 text-right">{formatINR(r.amount)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(r.approved_amount)}</td>
                  <td className="px-3 py-2 text-right">
                    {formatINR(r.paid_amount)}
                    {(r.flags || []).includes("paid_outside_period") && <span title="Paid outside selected period" className="text-slate-400">*</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{formatINR(r.remaining_amount)}</td>
                  <td className="px-3 py-2">
                    <span className={`border ${STATUS_CLS[r.status] || "bg-slate-100 border-slate-300"} px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold`}>
                      {STATUS_LABEL[r.status] || (r.status || "—").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.flags || []).map((f) => {
                        const meta = FLAG_META[f] || { label: f.toUpperCase(), cls: "bg-slate-100 border-slate-300", icon: AlertTriangle };
                        const Icon = meta.icon;
                        return (
                          <span key={f} className={`border ${meta.cls} px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold flex items-center gap-1`}>
                            <Icon size={9} />{meta.label}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const FilterDate = ({ label, testid, value, onChange }) => (
  <div>
    <div className="label-micro mb-1">{label}</div>
    <input
      data-testid={testid}
      type="date" value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none"
    />
  </div>
);

const FilterSelect = ({ label, testid, value, onChange, options }) => (
  <div>
    <div className="label-micro mb-1">{label}</div>
    <select
      data-testid={testid}
      value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none bg-transparent"
    >
      <option value="">ALL</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const FilterVendor = ({ testid, value, onChange, vendors }) => (
  <div>
    <div className="label-micro mb-1">VENDOR</div>
    <select
      data-testid={testid}
      value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none bg-transparent"
    >
      <option value="">ALL</option>
      {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
    </select>
  </div>
);

const KPI = ({ label, value, sub, hint, alert, danger }) => (
  <div className={`border-r border-b border-slate-200 bg-white p-4 lg:p-5 ${danger ? "bg-red-50/40" : ""}`}>
    <div className="label-micro">{label}</div>
    <div className="font-semibold text-2xl lg:text-3xl tracking-tight mt-1">{value}</div>
    {sub && <div className="font-mono text-[10px] text-slate-500 mt-1">{sub}</div>}
    {hint && <div className="font-mono text-[10px] text-slate-500 mt-1">{hint}</div>}
    {alert && (
      <div className="font-mono text-[10px] text-amber-700 mt-2 flex items-center gap-1">
        <CircleDollarSign size={10} /> {alert}
      </div>
    )}
  </div>
);

const FlagPill = ({ label, count, danger, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!count}
    className={`font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-1 border-2 ${
      count > 0
        ? (danger ? "bg-red-100 text-red-700 border-red-400 hover:bg-red-200" : "bg-olive/10 text-olive border-olive/40")
        : "bg-white text-slate-400 border-slate-300 cursor-default"
    }`}
  >
    {label} · {count}
  </button>
);
